import { useOrganizations } from '@contexts/organizations-provider';
import { ChangeRequest } from '@features/change-request/ChangeRequest';
import { useUserNavigation } from '@features/users/hooks/use-user-navigation/useUserNavigation';
import { useUser } from '@features/users/hooks/use-user/useUser';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';
import { useParams } from 'react-router-dom';

const UserChangeRequestRoute = React.memo((): React.ReactElement => {
  const { userId } = useParams<{ userId: string }>();
  const { selectedOrganization } = useOrganizations();
  const { navigateToUser } = useUserNavigation();

  const {
    data: user,
    isLoading,
    isError,
  } = useUser({
    organizationId: selectedOrganization?.id ?? '',
    userId: userId ?? '',
  });

  const backNavigationLabel = user
    ? `Edit user (${user.firstName} ${user.lastName})`
    : 'Edit user';

  const handleClose = React.useCallback((): void => {
    if (userId) {
      navigateToUser(userId);
    }
  }, [userId, navigateToUser]);

  if (isLoading) {
    return <ContentLoader />;
  }

  if (isError || !userId) {
    return <InlineFeedback type="data-error" />;
  }

  return (
    <ChangeRequest
      isOpen
      config={{
        type: 'UserUpdateRequest',
        metadata: { userId: userId },
      }}
      backNavigationLabel={backNavigationLabel}
      onClose={handleClose}
    />
  );
});

UserChangeRequestRoute.displayName = 'UserChangeRequestRoute';

export { UserChangeRequestRoute };
